import { DynamicReferenceType } from '~/enums/dynamicReferenceType'
import { ModelType } from '~/enums/modelType'
import { ReferenceNodeType } from "~/enums/referenceNodeType"
import { IDynamicReferencedThing } from '~/interfaces/dynamicFieldsInterfaces'
import { IHasParent, ITrackedModel } from '~/interfaces/modelInterfaces'
import { IReferencePath, IReferencePathNodeSpecific, IReferencePathServiceType } from '~/interfaces/referencePathInterfaces'
import IRepo from '~/interfaces/repo'
import Formula from '~/models/formula'
import { ReferencePathNode } from '~/models/referencePaths/referencePathNode'
import { RelationType } from '~/models/relationType'
import BuilderModel from '~/models/tracked/builderModel'
import ElementHandler from '~/services/elementHandler'
import { Logger } from "~/services/logger"
import ObjectModelRelationPathService from '~/services/referencePathServices/objectModelRelationPathService'
import Repo from '~/state/modules/repo'

export class ReferencePath extends BuilderModel implements IReferencePath {
  nodes: ReferencePathNode[] = []
  _nodesHandler: ElementHandler = new ElementHandler(this, ReferencePath, 'nodes')

  last_referenced_object?: Record<string, any>;

  get nodesHandler(): ElementHandler<IReferencePathNodeSpecific> {
    return this._nodesHandler
  }

  get nodesArray(): ReferencePathNode[] {
    return this.nodes
  }

  constructor(parent, json, options?: { nameInParent?: string, isCollectionRoot?: boolean }) {
    super()
    this.parent = parent
    this._nameInParent = options?.nameInParent || 'reference_paths'
    this._isCollectionRoot = options?.isCollectionRoot
    this.defineClientId(json?.client_id)
  }

  // ******************** RELATIONS AND REPOS ******************** //
  static repo: IRepo<ReferencePath> = new Repo<ReferencePath>('referencePaths', ReferencePath, {})
  repo: IRepo<this>

  get collectionKey() {
    return this._nameInParent
  }

  get rootName(): string {
    return this._nameInParent
  }

  addToRepo(): void {
    ReferencePath.repo.add(this)
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      "nodes": { type: ReferencePathNode, relationType: RelationType.array },
    }
  }

  createEmbeddedObject = {
    nodes: (parent, json) => {
      return ReferencePathNode.repo.findOrCreateWith(json.client_id, parent, json)
    },
  }

  initializeEmbedded(parent: IReferencePath & ITrackedModel, item: IReferencePathNodeSpecific & IHasParent<IReferencePath>, position: number = null, json: object = {}, selectItem: boolean = true) {
    item.triggerChanges = true
    item.parent = parent
    item.startTracking()
    this.placeInDictionary(item, item.rootName, this)
    parent.trackCreationDeletion(item.client_id, null, item.asJson(), item)
    return item
  }

  initializeEmbeddedNodes(nodes: ReferencePathNode[]): void {
    nodes.forEach(n => n.parent = this)
    this.nodes = nodes
  }

  // ******************** RELATIONS AND REPOS END ******************** //

  /**
   * Only need to look at last node to look up reference in repo
   */
  get dynamicFieldName(): string {
    return this.nodes[this.nodes.length - 1]?.service?.label;
  }

  get dynamicReferencedThing(): IDynamicReferencedThing {
    return {
      type: DynamicReferenceType.Reference,
      id: Formula.buildDynamicId({ id: this.client_id }),
      label: this.dynamicFieldName,
    }
  }

  get objectModelPathServices(): IReferencePathServiceType[] {
    // In Process Flow Builder, nodes always start with ReferenceNodeType.ProcessFlowVariable. Check first 2 nodes to determine path type.
    if (this.nodes[0]?.reference_type !== ReferenceNodeType.ObjectModel && this.nodes[1]?.reference_type !== ReferenceNodeType.ObjectModel) {
      Logger.errorOnce('ReferencePath#objectModelPathServices', 'Expected reference path nodes to begin with ReferenceNodeType.ObjectModel.');
      return [];
    }

    const objectModelNodes = this.nodes[0].reference_type === ReferenceNodeType.ObjectModel ? this.nodes : this.nodes.slice(1);
    return objectModelNodes.map(node => node.service);
  }

  get apiEndPointPathServices(): IReferencePathServiceType[] {
    if (this.nodes[0]?.reference_type !== ReferenceNodeType.ApiEndpoint && this.nodes[1]?.reference_type !== ReferenceNodeType.ApiEndpoint) {
      Logger.errorOnce('ReferencePath#apiEndPointPathServices', 'Expected reference path nodes to begin with ReferenceNodeType.ApiEndPoint.');
      return [];
    }
    const apiNodes = this.nodes[0].reference_type === ReferenceNodeType.ApiEndpoint ? this.nodes : this.nodes.slice(1);
    return apiNodes.map(node => node.service);
  }

  get formPathServices(): IReferencePathServiceType[] {
    // In Process Flow Builder, nodes always start with ReferenceNodeType.ProcessFlowVariable. Check first 2 nodes to determine path type.
    if (this.nodes[0]?.reference_type !== ReferenceNodeType.Form && this.nodes[1]?.reference_type !== ReferenceNodeType.Form) {
      Logger.errorOnce('ReferencePath#formPathServices', 'Expected reference path nodes to begin with ReferenceNodeType.Form.');
      return [];
    }
    const formNodes = this.nodes[0].reference_type === ReferenceNodeType.Form ? this.nodes : this.nodes.slice(1);
    return formNodes.map(node => node.service);
  }

  get nicePathString(): string {
    return this.nodes.slice(1).map(n => n.service.label).join(' > ')
  }

  get pathString(): string {
    const variablePathString = this.processFlowVariablePathString

    let pathString: string
    switch (this.nodesHandler.last?.reference_type) {
      case ReferenceNodeType.ObjectModelProperty:
        pathString = this.objectModelRelationPathString;
        break;
      case ReferenceNodeType.FormElement:
      case ReferenceNodeType.FormSection:
        pathString = this.formElementPathString;
        break;
      case ReferenceNodeType.ObjectModel:
      case ReferenceNodeType.ObjectModelLinkedForm:
        pathString = this.objectModelPathString;
        break;
      case ReferenceNodeType.Form:
        pathString = this.formPathString;
        break;
      case ReferenceNodeType.RecordHolder:
        pathString = this.recordHolderPathString;
        break;
      case ReferenceNodeType.ObjectModelRelation:
        pathString = this.accountRelationPathString;
        break;
      case ReferenceNodeType.OriginalSubmitter:
      case ReferenceNodeType.FormSubmissionID:
        pathString = this.formSubmissionDataPathString;
        break;
      case ReferenceNodeType.ApiMessageType:
        return variablePathString
        break
      case ReferenceNodeType.DynamicInput:
        pathString = this.apiDynamicInputPathString
        break
      case ReferenceNodeType.AccountRelationMetadata:
        pathString = this.accountRelationMetadataPathString;
        break;
      case ReferenceNodeType.CertificateDynamicInput:
        pathString = this.certificateDynamicInputPathString;
        break;
      default:
        return null;
    }
    if (variablePathString) pathString = `${variablePathString} > ${pathString}`
    return pathString
  }

  get processFlowVariablePathString(): string {
    const variableNode = this.nodes[0]?.reference_type === ReferenceNodeType.ProcessFlowVariable ? this.nodes[0] : null
    if (variableNode) return variableNode.service.label
  }

  /**
   * should not include the final node label. e.g. if OM > Rel > Prop, then this will not contain Prop label
   */
  get objectModelRelationPathString(): string {
    const services = this.objectModelPathServices;
    if (services.length === 0) return null;

    const rootLabel = services[0].label;
    const relationLabels = services.reduce((labels, service) => {
      if (service instanceof ObjectModelRelationPathService) labels.push(...service.breadcrumbs);
      return labels;
    }, [])
    return `${rootLabel} ${relationLabels.join(' and ')}`;
  }

  get objectModelPathString(): string {
    return this.objectModelRelationPathString
  }

  get recordHolderPathString(): string {
    return `${this.objectModelPathString} > User`
  }

  get accountRelationPathString(): string {
    const labels = this.nodes.slice(1, -1).map((n) => n.service.label)
    labels.push('Relationship');
    return labels.join(' > ');
  }

  get accountRelationMetadataPathString(): string {
    const labels = this.nodes.slice(1, -2).map((n) => n.service.label)
    labels.push('Relationship');
    return labels.join(' > ');
  }

  get apiDynamicInputPathString(): string {
    const labels = this.nodes.slice(2, -1).map((n) => n.service.label)

    return labels.join(' > ')
  }

  get certificateDynamicInputPathString(): string {
    const labels = this.nodes.slice(1, -1).map(n => n.service.label);
    return labels.join(' > ');
  }

  /**
   * there should only be one node, which is for the form itself, so path is blank since final node is not included
   */
  get formPathString(): string {
    return ''
  }

  /**
   * should not include the final node label. e.g. if Form > Rep > Rep > El, then this will not contain El label
   */
  get formElementPathString(): string {
    return this.formPathServices.reduce((labels, service) => {
      labels.push(...service.breadcrumbs)
      return labels
    }, [] as string[]).join(' > ')
  }

  get formSubmissionDataPathString(): string {
    const labels = this.nodes.slice(1, -1).map((n) => n.service.label)
    labels.push('Submission Metadata');
    return labels.join(' > ');
  }

  
  get lastNodeService(): IReferencePathServiceType {
    return this.lastNode?.service
  }

  get lastNode(): IReferencePathNodeSpecific {
    return this.nodesHandler.last
  }

  get firstNode(): IReferencePathNodeSpecific {
    return this.nodesHandler.first
  }

  /**
   * Go to last node and check its presence
   */
  get referenceExists(): boolean {
    return Boolean(this.lastNodeService?.referenceExists)
  }

  /**
   * Check every node's reference exists
   */
  get allReferencesExist(): boolean {
    return this.nodesHandler.all.every(n => n.service.referenceExists)
  }

  get allReferencesLinked(): boolean {
    const nodes = this.nodesHandler.all
    return nodes.every((currentNode, index) => {
      const nextNode = this.nodesHandler.all[index + 1] as IReferencePathNodeSpecific
      return currentNode.service.referenceExists && currentNode.canLinkTo(nextNode)
    })
  }

  /**
   * Special case when path starts with a Process Flow Variable and its variable is one that can be single or many (e.g. repeatable grid/stack form element)
   *   In this case need to override the rep grid/stack's +isCollection+ to match that of the variable's +isCollection+
   *   This is how a path can represent a single instance of a repeatable stack/grid
   */
  get referenceIsCollection(): boolean {
    const firstNode = this.firstNode
    let variable
    if (firstNode.reference_type === ReferenceNodeType.ProcessFlowVariable) {
      variable = firstNode.reference()
    }

    return this.nodesHandler.all.some((currentNode, index) => {
      if (variable && variable.variable_type === ModelType.DynamicInput && currentNode.reference_id === variable.reference_id) {
        return firstNode.service.referenceIsCollection
      } else {
        return currentNode.service.referenceIsCollection
      }
    })
  }

  get referenceIsSingle(): boolean {
    return !this.referenceIsCollection
  }

  get isDateTime(): boolean {
    return this.lastNode?.service.referenceGenericModelType === ModelType.DateTime
  }
}